import React, { useEffect, useState } from 'react';
import { Button, Col, Drawer, Popconfirm, Row, Space, Table, Typography, Card, Tooltip, Switch, Modal, Select } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { openNotification } from '../../../utils';
import { Link, useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { listReportFormats, deleteReportFormat, cloneReportFormat, setReportFormatActive } from '../services/report-formatService';
import ReportFormatForm from './Form';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import AttributeService from 'services/AttributeService';

export default function ReportFormatList({ referenceId }: { referenceId?: number }){
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [activatingId, setActivatingId] = useState<number | null>(null);
  const [cloneDrawerOpen, setCloneDrawerOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<number | null>(null);
  const [cloneTargetAttributeId, setCloneTargetAttributeId] = useState<number | undefined>();
  const [cloneOptions, setCloneOptions] = useState<{ value: number; label: string }[]>([]);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneOptionsLoading, setCloneOptionsLoading] = useState(false);
  
  const location = useLocation();
  const params = useParams();
  const [attributeId, setAttributeId] = useState<number | undefined>(undefined);
  const [attributeName, setAttributeName] = useState<string>('');

  const fetchData = async () => {
    const query = attributeId ? 'attributeId.equals=' + attributeId : undefined;
    const res = await listReportFormats(page-1, pageSize, 'title,asc', query);
    setData(res.content);
    setTotal(res.totalElements);
  };
  useEffect(() => {
    // If route has attribute id but state not set yet, avoid unfiltered fetch
    if (params.id && attributeId === undefined) return;
    fetchData();
  }, [page, pageSize, attributeId, params.id]);

  useEffect(() => {
    const aidParam = params.id as string | undefined;
    if (aidParam) {
      const num = Number(aidParam);
      setAttributeId(isNaN(num) ? undefined : num);
      if (!isNaN(num)) {
        AttributeService.get(num)
          .then(res => setAttributeName(res.data?.name || ''))
          .catch(() => setAttributeName(''));
      }
    } else {
      setAttributeId(undefined);
      setAttributeName('');
    }
  }, [params.id]);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id:number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleDelete = async (id:number) => {
    try {
      await deleteReportFormat(id);
      openNotification('success', 'Başarılı', 'Rapor Formatı Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Rapor Formatı silme işlemi başarısız');
    }
  };
  const normalizeText = (value?: string) =>
    (value || '')
      .toString()
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const loadCloneOptions = async () => {
    if (cloneOptions.length || cloneOptionsLoading) {
      return;
    }
    setCloneOptionsLoading(true);
    try {
      const res = await AttributeService.getAll();
      const activeOptions = (res.data || [])
        .filter((attr: any) => attr?.isActive)
        .map((attr: any) => ({ value: attr.id, label: attr.name }))
        .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, 'tr'));
      setCloneOptions(activeOptions);
    } catch (e) {
      openNotification('error', 'Hata', 'Parametreler yüklenemedi');
    } finally {
      setCloneOptionsLoading(false);
    }
  };

  const openCloneDrawer = async (id: number) => {
    setCloneSourceId(id);
    setCloneTargetAttributeId(attributeId);
    setCloneDrawerOpen(true);
    await loadCloneOptions();
  };

  const handleClone = async () => {
    if (!cloneSourceId || !cloneTargetAttributeId) {
      openNotification('error', 'Hata', 'Lütfen bir parametre seçin');
      return;
    }
    setCloneLoading(true);
    try {
      await cloneReportFormat(cloneSourceId, cloneTargetAttributeId);
      openNotification('success', 'Başarılı', 'Rapor Formatı kopyalandı');
      setCloneDrawerOpen(false);
      setCloneSourceId(null);
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Rapor Formatı kopyalanamadı');
    } finally {
      setCloneLoading(false);
    }
  };

  const closeCloneDrawer = () => {
    setCloneDrawerOpen(false);
    setCloneSourceId(null);
  };

  const performToggleActive = async (record: any) => {
    if (!record?.id) return;
    setActivatingId(record.id);
    try {
      await setReportFormatActive(record.id, true);
      openNotification('success', 'Başarılı', 'Rapor formatı aktifleştirildi.');
      await fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Rapor formatı durumu güncellenemedi.');
    } finally {
      setActivatingId(null);
    }
  };

  const handleToggleActive = (record: any, checked: boolean) => {
    if (!checked) {
      return;
    }
    Modal.confirm({
      title: 'Rapor formatını aktifleştirmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'İptal',
      onOk: () => performToggleActive(record),
    });
  };

  const columns = [
    { title: 'Başlık', dataIndex: 'title', sorter: true },
    { title: 'Alt Başlık', dataIndex: 'subTitle', sorter: true },
    { title: 'Sürüm', dataIndex: 'version', sorter: true },
    { title: 'Yayın Tarihi', dataIndex: 'publishDate', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'Doküman No', dataIndex: 'documentNo', sorter: true },
    {
      title: 'Aktif',
      dataIndex: 'isActive',
      render: (_: any, record: any) => (
        <Switch
          checked={Boolean(record?.isActive)}
          onChange={(checked) => handleToggleActive(record, checked)}
          loading={activatingId === record?.id}
          disabled={Boolean(record?.isActive) || (activatingId !== null && activatingId !== record?.id)}
        />
      ),
    },
    { title: 'İşlemler', render: (_:any, r:any) => (
      <Space>
        <Link to={'/report-formats/detail/' + r.id}>
          <Button type='default' className='d-button action-button'>
            İşlemler
          </Button>
        </Link>
        <Tooltip title='Kopyala'>
          <Button shape='circle' className='icon-button' onClick={() => openCloneDrawer(r.id)} icon={<CopyOutlined />} />
        </Tooltip>
        <Tooltip title='Düzenle'>
          <Button
            icon={<img src={PenIcon} alt="pen" className="icon" />}
            shape="circle"
            onClick={() => openEdit(r.id)}
            className="icon-button"
          />
        </Tooltip>
        <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
          <Tooltip title='Sil'>
            <Button
              icon={<img src={TrashIcon} alt="trash" className="icon" />}
              shape="circle"
              className="icon-button ml-12px"
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    ) }
  ];
  return (
    <>
      
      <Card
        className='br-30px'
      >
        <Row justify={'space-between'} align='middle' className='mb-16px'>
          <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>{attributeName ? `${attributeName}` : ''}</Typography.Title>
          </Col>
          <Col>
            <Space>
            <Button type='primary' onClick={openCreate}>Ekle</Button>
            <Link to='/settings/parameters'>
              <Button type='primary' className='d-button action-button'>Geri</Button>
            </Link>
          </Space>
          </Col>
        </Row>
        <Table rowKey='id' dataSource={data} columns={columns} pagination={{ current: page, pageSize, total, onChange:(p,s)=>{setPage(p); setPageSize(s);} }} />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={900} title={editingId ? 'Rapor Formatı Düzenle' : 'Rapor Formatı Oluştur'}>
        <ReportFormatForm id={editingId} referenceId={attributeId} onSuccess={() => { handleClose(); fetchData();}} />
      </Drawer>
      <Drawer
        open={cloneDrawerOpen}
        onClose={closeCloneDrawer}
        destroyOnClose
        width={480}
        title='Rapor Formatını Kopyala'
        footer={(
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={closeCloneDrawer} disabled={cloneLoading}>Vazgeç</Button>
              <Button type='primary' onClick={handleClone} loading={cloneLoading} disabled={!cloneTargetAttributeId}>
                Kaydet
              </Button>
            </Space>
          </div>
        )}
      >
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <Typography.Text strong>Parametre Seçiniz</Typography.Text>
          <Select
            showSearch
            placeholder='Parametre seçiniz'
            options={cloneOptions}
            loading={cloneOptionsLoading}
            value={cloneTargetAttributeId}
            onChange={(value) => setCloneTargetAttributeId(value)}
            filterOption={(input, option) =>
              normalizeText(option?.label as string).includes(normalizeText(input))
            }
            optionFilterProp='label'
            style={{ width: '100%' }}
          />
        </Space>
      </Drawer>
    </>
  );
}
